import {
  useV2CreateChangeOrderMutation,
  useV2UpdateChangeOrderMutation,
  useV2DeleteChangeOrderMutation,
} from "@generated";
import type { V2ChangeOrderResource } from "@generated";
import type { ChangeOrder, ChangeOrderFormValues } from "../types";
import { toChangeOrder } from "./useChangeOrder";

// The editable attributes; blank optional strings are sent as null so a user
// can clear a saved value.
const editableAttributes = (values: ChangeOrderFormValues) => ({
  cause: values.cause || null,
  "cause-note": values.causeNote || null,
  "external-reference": values.externalReference || null,
  "external-url": values.externalUrl || null,
});

// The API rejects source-items on a reference-only change order, so they are
// only sent for an authored one (where an empty list is valid).
export const buildChangeOrderData = (
  values: ChangeOrderFormValues,
  parentProjectId: string
): V2ChangeOrderResource =>
  ({
    type: "change-orders",
    attributes: {
      ...editableAttributes(values),
      "reference-only": values.referenceOnly,
      ...(values.referenceOnly ? {} : { "source-items": values.sourceItems }),
    },
    relationships: {
      "parent-project": { data: { type: "projects", id: parentProjectId } },
    },
  } as unknown as V2ChangeOrderResource);

// Only the four editable attributes can change after create; the generated
// type marks relationships required, hence the cast.
export const buildChangeOrderUpdate = (
  id: string,
  values: ChangeOrderFormValues
): V2ChangeOrderResource =>
  ({
    type: "change-orders",
    id,
    attributes: editableAttributes(values),
  } as unknown as V2ChangeOrderResource);

export const useChangeOrderMutations = () => {
  const [create] = useV2CreateChangeOrderMutation();
  const [update] = useV2UpdateChangeOrderMutation();
  const [destroy] = useV2DeleteChangeOrderMutation();

  return {
    create: async (
      values: ChangeOrderFormValues,
      parentProjectId: string
    ): Promise<ChangeOrder> => {
      const res = await create({
        body: { data: buildChangeOrderData(values, parentProjectId) },
      }).unwrap();
      return toChangeOrder(res.data as V2ChangeOrderResource);
    },
    update: (id: string, values: ChangeOrderFormValues) =>
      update({
        id,
        body: { data: buildChangeOrderUpdate(id, values) },
      }).unwrap(),
    remove: (id: string) => destroy({ id }).unwrap(),
  };
};

export default useChangeOrderMutations;
